import { Fragment, ReactNode, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link, useLocation } from 'react-router-dom';
import { XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface SidebarProps {
  children: ReactNode;
}

interface SidebarHeaderProps {
  children: ReactNode;
}

interface SidebarBodyProps {
  children: ReactNode;
}

interface SidebarSectionProps {
  children: ReactNode;
}

interface SidebarItemProps {
  href: string;
  children: ReactNode;
  current?: boolean;
}

interface SidebarLabelProps {
  children: ReactNode;
}

/**
 * Mobile sidebar with slide-out menu
 */
export function Sidebar({ children }: SidebarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      {/* Mobile menu button */}
      <div className="sticky top-0 z-40 flex h-16 items-center gap-x-4 border-b border-primary-950/10 bg-primary-900 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:hidden">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-primary-100 lg:hidden"
          onClick={() => setMobileMenuOpen(true)}
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        </button>
        <div className="flex-1 text-xs font-semibold leading-6 text-white">My Masjid Connect</div>
      </div>

      {/* Mobile menu */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileMenuOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-primary-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-20 flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>

                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-primary-900 px-3 pb-4 ring-1 ring-white/10">
                  <div className="flex h-16 shrink-0 items-center justify-center">
                    <span className="text-lg font-bold text-white">MMC</span>
                  </div>
                  <nav className="flex flex-1 flex-col" onClick={() => setMobileMenuOpen(false)}>
                    {children}
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}

/**
 * Sidebar header section
 */
export function SidebarHeader({ children }: SidebarHeaderProps) {
  return <div className="mb-4">{children}</div>;
}

/**
 * Sidebar body section
 */
export function SidebarBody({ children }: SidebarBodyProps) {
  return <div className="flex flex-1 flex-col gap-y-7">{children}</div>;
}

/**
 * Sidebar section - groups sidebar items
 */
export function SidebarSection({ children }: SidebarSectionProps) {
  return (
    <ul role="list" className="-mx-2 space-y-1">
      {children}
    </ul>
  );
}

/**
 * Individual sidebar item/link
 */
export function SidebarItem({ href, children, current }: SidebarItemProps) {
  const location = useLocation();
  const isActive = current !== undefined ? current : location.pathname === href;

  return (
    <li>
      <Link
        to={href}
        className={clsx(
          'group flex items-center justify-center rounded-md p-3 text-sm font-semibold leading-6 transition-colors',
          isActive
            ? 'bg-primary-800 text-white'
            : 'text-primary-100 hover:bg-primary-800 hover:text-white'
        )}
      >
        {children}
      </Link>
    </li>
  );
}

/**
 * Sidebar label for displaying text
 */
export function SidebarLabel({ children }: SidebarLabelProps) {
  return <span>{children}</span>;
}
